import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import { ExplorerStep } from './ExplorerStep';
import { exportMarkdownToDocx } from '../../utils/exportDocx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Button,
  Input,
  Card,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface Step {
  reasoning: string;
  cypher: string;
  results: Record<string, unknown>[] | null;
  columns: string[];
  isComplete: boolean;
}

interface AIExplorerProps {
  projectId: string;
  onHighlight: (elementIds: string[]) => void;
}

// Normalize Cypher for loop detection
function normalizeCypher(cypher: string): string {
  return cypher
    .toLowerCase()
    .replace(/'[^']*'/g, "'?'")
    .replace(/"[^"]*"/g, '"?"')
    .replace(/\b\d+\b/g, 'N')
    .replace(/\s+/g, ' ')
    .trim();
}

// Silently auto-append RETURN clause if missing
function autoFixReturnClause(cypher: string): string {
  if (!cypher) return cypher;
  const stripped = cypher.replace(/'[^']*'/g, '').replace(/"[^"]*"/g, '');
  if (!/\bRETURN\b/i.test(stripped)) {
    const vars = cypher.match(/\((\w+)[:\s{)]/g)?.map(m => m.match(/\((\w+)/)?.[1]).filter(Boolean) || [];
    const returnClause = vars.length > 0 ? ` RETURN ${vars.join(', ')}` : ' RETURN *';
    return cypher.trimEnd() + returnClause;
  }
  return cypher;
}

const SOFT_WARNING_INTERVAL = 20;

export const AIExplorer: React.FC<AIExplorerProps> = ({ projectId, onHighlight }) => {
  const { theme } = useTheme();
  const { fetchData } = useAuthenticatedFetch();
  const [question, setQuestion] = useState('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [stopped, setStopped] = useState(false);
  const [loopWarning, setLoopWarning] = useState<{ stepNum: number; newCypher: string } | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [reportError, setReportError] = useState(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [continuingStep, setContinuingStep] = useState<number | null>(null);
  const executedQueriesRef = useRef<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const askAI = useCallback(async (message: string) => {
    setIsThinking(true);
    try {
      const response = await fetchData(`/dastarkhan/projects/${projectId}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message,
          session_id: sessionId,
          context_level: 'schema_sample',
        }),
      }) as { answer: string; cypher_query?: string } | undefined;

      if (response) {
        const answer = response.answer;
        let cypher = response.cypher_query || '';
        if (!cypher) {
          const match = answer.match(/CYPHER:\s*(.+?)(?:\n\n|\n(?=[A-Z])|$)/s);
          if (match) cypher = match[1].trim();
        }
        if (!cypher) {
          const match = answer.match(/(MATCH[\s\S]*?RETURN[\s\S]*?)(?:\n\n|$)/);
          if (match) cypher = match[1].trim();
        }
        const reasoning = cypher
          ? answer.replace(/CYPHER:[\s\S]*$/, '').replace(cypher, '').trim()
          : answer;
        return { reasoning: reasoning || answer, cypher };
      }
      return null;
    } catch {
      return { reasoning: 'The system encountered an error processing the request.', cypher: '' };
    } finally {
      setIsThinking(false);
    }
  }, [fetchData, projectId, sessionId]);

  const checkForLoop = (cypher: string): number | null => {
    if (!cypher) return null;
    const normalized = normalizeCypher(cypher);
    for (let i = 0; i < executedQueriesRef.current.length; i++) {
      if (executedQueriesRef.current[i] === normalized) return i + 1;
    }
    return null;
  };

  const handleStart = async () => {
    if (!question.trim()) return;
    setStopped(false);
    setSteps([]);
    setReport(null);
    setReportError(false);
    setLoopWarning(null);
    executedQueriesRef.current = [];

    const prompt = `The following investigation question requires systematic analysis through the Neo4j graph:

"${question.trim()}"

Generate the first query to begin the investigation. Provide analytical reasoning for the approach, then the Cypher query.
Format: analytical reasoning, then on a separate line CYPHER: followed by a complete read-only Cypher query.
CRITICAL: Every query MUST end with a RETURN clause. A bare MATCH without RETURN is invalid Cypher. Always use MATCH...RETURN.`;

    const result = await askAI(prompt);
    if (result) {
      // Silently fix RETURN clause before storing
      const fixedCypher = autoFixReturnClause(result.cypher);
      setSteps([{ reasoning: result.reasoning, cypher: fixedCypher, results: null, columns: [], isComplete: false }]);
    }
  };

  const handleExecuteStep = async (stepIndex: number, cypher: string, retryCount = 0) => {
    // RETURN clause should already be appended — just in case, fix silently
    cypher = autoFixReturnClause(cypher);
    setSteps((prev) => prev.map((s, i) => i === stepIndex ? { ...s, cypher } : s));

    if (retryCount === 0) executedQueriesRef.current.push(normalizeCypher(cypher));

    try {
      const result = await fetchData(`/dastarkhan/projects/${projectId}/cypher`, {
        method: 'POST',
        body: JSON.stringify({ query: cypher }),
      }) as { data: Record<string, unknown>[]; columns: string[] } | undefined;

      if (result) {
        setSteps((prev) => prev.map((s, i) =>
          i === stepIndex ? { ...s, cypher, results: result.data, columns: result.columns, isComplete: true } : s
        ));
        // Highlight results on the main graph canvas
        const ids: string[] = [];
        for (const row of result.data) {
          for (const val of Object.values(row)) {
            if (val && typeof val === 'object' && 'element_id' in (val as Record<string, unknown>)) {
              ids.push((val as Record<string, unknown>).element_id as string);
            }
          }
        }
        if (ids.length > 0) onHighlight(ids);
      }
    } catch (err: unknown) {
      const detail = (err && typeof err === 'object' && 'message' in err) ? String((err as { message: string }).message) : 'Query execution failed';

      if (retryCount < 1) {
        const fixResult = await askAI(
          `The following Cypher query failed with this error:\n\nQuery: ${cypher}\nError: ${detail}\n\nPlease fix the query. Return ONLY the corrected Cypher query after CYPHER: — no explanation needed. The query MUST end with a RETURN clause.`
        );
        if (fixResult && fixResult.cypher) {
          const fixedCypher = autoFixReturnClause(fixResult.cypher);
          setSteps((prev) => prev.map((s, i) =>
            i === stepIndex ? { ...s, cypher: fixedCypher } : s
          ));
          await handleExecuteStep(stepIndex, fixedCypher, retryCount + 1);
          return;
        }
      }

      setSteps((prev) => prev.map((s, i) =>
        i === stepIndex ? { ...s, results: [{ error: `Cypher Error: ${detail}` }], columns: ['error'], isComplete: true } : s
      ));
    }
  };

  // Auto-scroll to bottom when new steps arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [steps, isThinking]);

  const handleContinue = async (stepIndex: number) => {
    setContinuingStep(stepIndex);
    setLoopWarning(null);

    const lastStep = steps[stepIndex];
    // Truncated summary for prompt (avoid token limits)
    const resultSummary = lastStep.results
      ? `Results from step ${stepIndex + 1} (${lastStep.results.length} rows): ${JSON.stringify(lastStep.results.slice(0, 2)).slice(0, 500)}`
      : 'No results from previous step.';

    const prompt = `Previous query results:

${resultSummary}

Based on these results, determine the next analytical step. Provide reasoning and the next Cypher query.
Format: reasoning, then CYPHER: followed by a complete read-only Cypher query.
CRITICAL: Every query MUST end with a RETURN clause. A bare MATCH without RETURN is invalid. Always MATCH...RETURN.
If the investigation is complete and the question has been answered, state "INVESTIGATION COMPLETE" and provide a concise summary of findings.`;

    const result = await askAI(prompt);
    if (result) {
      const isInvestigationComplete = result.reasoning.includes('INVESTIGATION COMPLETE');
      const fixedCypher = autoFixReturnClause(result.cypher);

      if (fixedCypher && !isInvestigationComplete) {
        const loopStep = checkForLoop(fixedCypher);
        if (loopStep !== null) {
          setLoopWarning({ stepNum: loopStep, newCypher: fixedCypher });
          setSteps((prev) => [...prev, {
            reasoning: result.reasoning, cypher: fixedCypher,
            results: null, columns: [], isComplete: false,
          }]);
          setContinuingStep(null);
          return;
        }
      }

      setSteps((prev) => [...prev, {
        reasoning: result.reasoning,
        cypher: fixedCypher,
        results: null,
        columns: [],
        isComplete: isInvestigationComplete,
      }]);
    }
    setContinuingStep(null);
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportError(false);
    try {
      // Truncate step summaries to avoid token limits
      const recentSteps = steps.slice(-10); // Max 10 steps
      const stepsHistory = recentSteps.map((s, i) => {
        let resultSummary = 'Not executed.';
        if (s.results && s.results.length > 0) {
          const firstRow = s.results[0];
          const keyFields = Object.entries(firstRow)
            .slice(0, 3)
            .map(([k, v]) => {
              if (v && typeof v === 'object' && !Array.isArray(v)) {
                const obj = v as Record<string, unknown>;
                const props = obj.properties as Record<string, unknown> | undefined;
                const name = props?.name || props?.Name || '';
                return name ? `${k}: ${name}` : `${k}: [object]`;
              }
              return `${k}: ${String(v).slice(0, 50)}`;
            })
            .join(', ');
          resultSummary = `${s.results.length} rows. First: ${keyFields}`;
        } else if (s.results) {
          resultSummary = '0 rows returned.';
        }
        return `Step ${i + 1}: ${s.reasoning.slice(0, 150)}... | Query: ${(s.cypher || 'N/A').slice(0, 100)} | Results: ${resultSummary}`;
      }).join('\n');

      const reportPrompt = `Generate a structured investigation report based on the following exploration.

INVESTIGATION QUESTION: "${question}"

EXPLORATION STEPS:
${stepsHistory}

REPORT FORMAT (use exactly these sections):
# Investigation Report

## Executive Summary
[2-3 sentence summary of what was investigated and the key finding]

## Investigation Question
[The original question being investigated]

## Methodology
[Numbered list of analytical steps taken, each with the Cypher query used]

## Key Findings
[Bulleted findings with supporting data from query results]

## Entity Chain Analysis
[If applicable: describe the chain of entities/relationships discovered]

## Conclusion
[Final assessment addressing the original investigation question]

STYLE: Third-person analytical voice. No first person. No exclamation marks. No dramatic language.`;

      const response = await fetchData(`/dastarkhan/projects/${projectId}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: reportPrompt,
          session_id: crypto.randomUUID(), // Fresh session to avoid context overflow
          context_level: 'schema_data',
        }),
      }) as { answer: string } | undefined;

      if (response) {
        setReport(response.answer);
      }
    } catch (err: unknown) {
      const detail = (err && typeof err === 'object' && 'message' in err) ? String((err as { message: string }).message) : 'Unknown error';
      setReport(`Report generation failed: ${detail}`);
      setReportError(true);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadMd = () => {
    if (!report) return;
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investigation-report-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setExportNotice(null);
    try {
      await exportMarkdownToDocx(report, `investigation-report-${new Date().toISOString().slice(0, 10)}.docx`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'DOCX export failed';
      setExportNotice(`${msg} — downloaded Markdown instead.`);
      handleDownloadMd();
    }
  };

  const completedSteps = steps.filter((s) => s.isComplete).length;
  const showSoftWarning = completedSteps > 0 && completedSteps % SOFT_WARNING_INTERVAL === 0;

  return (
    <Stack gap="md">
      <Card>
        <Card.Body>
          <Stack gap="sm">
            <Flex justify="between" align="center">
              <span style={{ fontSize: '0.95rem', fontWeight: 600, color: theme.colors.text.primary }}>
                AI Explorer
              </span>
              <Flex gap="xs">
                {steps.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setSteps([]);
                    setQuestion('');
                    setReport(null);
                    setReportError(false);
                    setStopped(false);
                    setLoopWarning(null);
                    setContinuingStep(null);
                    executedQueriesRef.current = [];
                  }}>Clear</Button>
                )}
                {completedSteps >= 3 && (
                  <Button variant="outline" size="sm" onClick={handleGenerateReport} disabled={generatingReport}>
                    {generatingReport ? 'Generating...' : 'Generate Report'}
                  </Button>
                )}
              </Flex>
            </Flex>
            <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.text.secondary }}>
              Ask a complex investigation question. The AI will generate and execute a chain of queries step-by-step, with your approval at each step.
            </p>
            <Flex gap="sm" align="end">
              <div style={{ flex: 1 }}>
                <textarea
                  value={question}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
                  placeholder="e.g., How is Viktor Petrov connected to the London property?"
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleStart(); } }}
                  autoComplete="one-time-code"
                  rows={2}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    fontSize: '0.85rem',
                    fontFamily: "'Source Sans 3', sans-serif",
                    padding: spacing.xs,
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: '4px',
                    background: theme.colors.background.default,
                    color: theme.colors.text.primary,
                    lineHeight: 1.4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <Button variant="solid" color="primary" size="sm" onClick={handleStart} disabled={isThinking || !question.trim()}>
                {isThinking && steps.length === 0 ? 'Analyzing...' : 'Explore'}
              </Button>
            </Flex>
          </Stack>
        </Card.Body>
      </Card>

      {/* Steps */}
      {steps.map((step, i) => {
        const isInvestigationComplete = step.reasoning.includes('INVESTIGATION COMPLETE');
        const isLast = i === steps.length - 1;
        return (
          <ExplorerStep
            key={i}
            stepNumber={i + 1}
            reasoning={step.reasoning}
            suggestedCypher={step.cypher}
            results={step.results}
            columns={step.columns}
            isExecuting={false}
            isComplete={step.isComplete}
            isContinuing={continuingStep === i}
            isLastStep={isLast}
            isInvestigationComplete={isInvestigationComplete}
            generatingReport={generatingReport}
            onExecute={(cypher) => handleExecuteStep(i, cypher)}
            onContinue={() => handleContinue(i)}
            onStop={() => setStopped(true)}
            onGenerateReport={handleGenerateReport}
            onHighlightStep={step.results && step.results.length > 0 ? () => {
              const ids: string[] = [];
              for (const row of step.results!) {
                for (const val of Object.values(row)) {
                  if (val && typeof val === 'object' && 'element_id' in (val as Record<string, unknown>)) {
                    ids.push((val as Record<string, unknown>).element_id as string);
                  }
                }
              }
              if (ids.length > 0) onHighlight(ids);
            } : undefined}
          />
        );
      })}

      {/* Loop detection warning */}
      {loopWarning && (
        <Card>
          <Card.Body>
            <Stack gap="sm">
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.colors.semantic.warning.default }}>
                Potential circular pattern detected
              </span>
              <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                The suggested query appears similar to Step {loopWarning.stepNum}.
              </p>
              <Flex gap="sm">
                <Button variant="outline" size="sm" onClick={() => setLoopWarning(null)}>Continue Anyway</Button>
                <Button variant="outline" size="sm" onClick={() => { setLoopWarning(null); setStopped(true); }}>Stop</Button>
              </Flex>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {showSoftWarning && !stopped && (
        <Card>
          <Card.Body>
            <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.semantic.warning.default }}>
              {completedSteps} steps completed. Consider generating a report.
            </p>
          </Card.Body>
        </Card>
      )}

      {isThinking && steps.length > 0 && (
        <Card>
          <Card.Body>
            <span style={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>Analyzing next step...</span>
          </Card.Body>
        </Card>
      )}

      {stopped && (
        <Card>
          <Card.Body>
            <Flex justify="between" align="center">
              <span style={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                Exploration stopped. Review the results above.
              </span>
              {completedSteps >= 3 && (
                <Button variant="outline" size="sm" onClick={handleGenerateReport} disabled={generatingReport}>
                  {generatingReport ? 'Generating...' : 'Generate Report'}
                </Button>
              )}
            </Flex>
          </Card.Body>
        </Card>
      )}

      {/* Report display */}
      {report && (
        <Card>
          <Card.Body>
            <Stack gap="sm">
              <Flex justify="between" align="center">
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: theme.colors.text.primary }}>
                  Investigation Report
                </span>
                <Flex gap="xs">
                  {reportError ? (
                    <Button variant="outline" size="sm" onClick={handleGenerateReport} disabled={generatingReport}>
                      {generatingReport ? 'Retrying...' : 'Retry'}
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={handleDownloadMd}>Download .md</Button>
                      <Button variant="solid" size="sm" onClick={handleDownloadDocx}>Download .docx</Button>
                    </>
                  )}
                </Flex>
              </Flex>
              {exportNotice && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.semantic.warning.default }}>
                  {exportNotice}
                </p>
              )}
              <div style={{
                fontSize: '0.85rem',
                color: theme.colors.text.primary,
                lineHeight: 1.6,
                maxHeight: '500px',
                overflow: 'auto',
                padding: spacing.sm,
                background: theme.colors.background.elevated,
                borderRadius: '4px',
              }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 style={{ fontSize: '1.3rem', fontWeight: 700, margin: '16px 0 8px', color: theme.colors.text.primary }}>{children}</h1>,
                    h2: ({ children }) => <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '14px 0 6px', color: theme.colors.text.primary }}>{children}</h2>,
                    h3: ({ children }) => <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '12px 0 4px', color: theme.colors.text.primary }}>{children}</h3>,
                    p: ({ children }) => <p style={{ margin: '6px 0', color: theme.colors.text.primary }}>{children}</p>,
                    ul: ({ children }) => <ul style={{ margin: '6px 0', paddingLeft: '20px' }}>{children}</ul>,
                    ol: ({ children }) => <ol style={{ margin: '6px 0', paddingLeft: '20px' }}>{children}</ol>,
                    li: ({ children }) => <li style={{ margin: '2px 0', color: theme.colors.text.primary }}>{children}</li>,
                    code: ({ children, className }) => {
                      const isBlock = className?.startsWith('language-');
                      if (isBlock) {
                        return <pre style={{ background: theme.colors.background.paper, padding: spacing.sm, borderRadius: '4px', overflow: 'auto', fontSize: '0.8rem', fontFamily: 'monospace' }}><code>{children}</code></pre>;
                      }
                      return <code style={{ background: theme.colors.background.paper, padding: '1px 4px', borderRadius: '3px', fontSize: '0.8rem', fontFamily: 'monospace' }}>{children}</code>;
                    },
                    strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                    table: ({ children }) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '8px 0' }}>{children}</table>,
                    th: ({ children }) => <th style={{ border: `1px solid ${theme.colors.border.default}`, padding: '4px 8px', background: theme.colors.background.paper, fontWeight: 600, textAlign: 'left', fontSize: '0.8rem' }}>{children}</th>,
                    td: ({ children }) => <td style={{ border: `1px solid ${theme.colors.border.default}`, padding: '4px 8px', fontSize: '0.8rem' }}>{children}</td>,
                  }}
                >
                  {report}
                </ReactMarkdown>
              </div>
            </Stack>
          </Card.Body>
        </Card>
      )}
      <div ref={bottomRef} />
    </Stack>
  );
};
